import React from 'react';
export type DateParam = Date;

export interface IDateParts {
    day: number,
    month:  number,
    year:  number,
    hours:  number,
    minutes:  number,
    seconds:  number,
    milliseconds:  number,
}

export default function useFormatter () {
    const toFloat = React.useCallback((value: number | null | undefined, precision: number = 2) => {
        if (value !== null && value !== undefined) {
            const config = {
                minimumFractionDigits: precision, 
                maximumFractionDigits: precision
            };

            return new Intl.NumberFormat("pt-BR", config).format(value);
        }

        return value;
    }, []);

    const parseDate = React.useCallback((value: string | Date | null | undefined) : Date | null => {
        if (value !== null && value !== undefined) {
            return typeof value === "string" ? new Date(value) : value;
        }

        return null;
    }, []);

    const toDate = React.useCallback((value: string | Date | null | undefined) => {
        const date = parseDate(value);

        if (date !== null) {
            return date.toLocaleDateString(); 
        }

        return "";
    }, [parseDate]);

    const toDateTime = React.useCallback((value: string | Date | null | undefined) => {
        const date = parseDate(value);

        if (date !== null) {
            return date.toLocaleString(); 
        }

        return "";
    }, [parseDate]);

    const toTime = React.useCallback((value: string | Date | null | undefined) => {
        const date = parseDate(value);

        if (date !== null) {
            return date.toLocaleTimeString(); 
        }

        return "";
    }, [parseDate]);

    const toCompetencia = React.useCallback((value: string | null | undefined) => {
        if (value === null || value === undefined) {
            return "";
        }

        let year = 0;
        let month = 0;
        if (value.length === 4) {
            year = parseInt(value.substr(0, 2)) + 2000;
            month = parseInt(value.substr(2));
        }
        else if (value.length === 6) {
            year = parseInt(value.substr(0, 4));
            month = parseInt(value.substr(4));
        }

        if (year > 0 && month > 0) {
            const date = new Date(year, month - 1, 1);
            const monthName = date.toLocaleString('default', { month: 'long' });

            return `${monthName}/${year.toString()}`;
        }

        return "";
    }, []);

    const extractNumbers = React.useCallback((value: string) => {
        return (value || "").toString().replace(/\D/g, "");
    }, []);

    const toCelular = React.useCallback((value: string | null | undefined) => {
        if (value === null || value === undefined) {
            return "";
        }

        const numbers = extractNumbers(value);
        
        if (numbers.length !== 11) {
            return numbers;
        }
        //01234567890
        //32988160347
        var ddd = numbers.substr(0, 2);
        var nove = numbers.substr(2, 1);
        var prefixo = numbers.substr(3, 4);
        var numero = numbers.substr(7);

        return `(${ddd})${nove}.${prefixo}-${numero}`;
    }, [extractNumbers]);

    const toTelefone = React.useCallback((value: string | null | undefined) => {
        if (value === null || value === undefined) {
            return "";
        }
        
        const numbers = extractNumbers(value);
        
        if (numbers.length < 10 || numbers.length > 11) {
            return numbers;
        }
        
        if (numbers.length === 10) {
            var ddd = numbers.substr(0, 2);
            var nove = numbers.substr(2, 1);
            var prefixo = numbers.substr(2, 4);
            var numero = numbers.substr(6);
    
            return `(${ddd})${nove}.${prefixo}-${numero}`;
        }
        else {
            return toCelular(numbers);
        }
    }, [extractNumbers, toCelular]);


    const isValidDate = React.useCallback((value: Date | null | undefined) => {
        return value !== null && value !== undefined && value.getDate && !isNaN(value.getDate());
    }, []);

    const toDateParts = React.useCallback((value: Date) : IDateParts => {
        return {
            day: value.getDate(),
            month: value.getMonth() - 1,
            year: value.getFullYear(),
            hours: value.getHours(),
            minutes: value.getMinutes(),
            seconds: value.getSeconds(),
            milliseconds: value.getMilliseconds()
        }
    }, []);

    const fromDateParts = React.useCallback((dateParts: IDateParts) => {
        return new Date(
            dateParts.year,
            dateParts.month + 1,
            dateParts.day,
            dateParts.hours,
            dateParts.minutes,
            dateParts.seconds,
            dateParts.milliseconds
        );
    }, []);

    const toDateObjectOnly = React.useCallback((value: Date | null | undefined) => {
        if (!isValidDate(value)) {
            return null;
        }

        const parts = toDateParts(value as Date);
        return fromDateParts({
            ...parts,
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0
        });

    }, [fromDateParts, isValidDate, toDateParts]);

    const toBooleanString = React.useCallback((value: string | boolean | null | undefined) => {
        let v = 
            value !== null 
            && value !== undefined
            && !!(typeof(value) === "string" ? parseInt(value) : value);

        return v ? "Sim" : "Não";
    }, []);

    const isValidCelularLength = React.useCallback((numero: string) => {
        return extractNumbers(numero).length === 11;
    }, [extractNumbers]);

    const toCpf = React.useCallback((value: string) => {
        const cpf = extractNumbers(value);
        const p1 = cpf.substring(0, 3);
        const p2 = cpf.substring(6, 3);
        const p3 = cpf.substring(9, 3);
        const p4 = cpf.substring(11, 2);

        return `${p1}.${p2}.${p3}-${p4}`;
    }, [extractNumbers]);

    const toCnpj = React.useCallback((value: string) => {
        const cnpj = extractNumbers(value);
        const p1 = cnpj.substring(0, 2);
        const p2 = cnpj.substring(2, 3);
        const p3 = cnpj.substring(5, 3);
        const p4 = cnpj.substring(8, 4);
        const p5 = cnpj.substring(12, 2);

        return `${p1}.${p2}.${p3}/${p4}-${p5}`;
    }, [extractNumbers]);

    return React.useMemo(() => ({
        toFloat, 
        toDate, 
        toTime, 
        toDateTime, 
        toCompetencia, 
        toCelular, 
        toTelefone, 
        extractNumbers, 
        isValidDate, 
        toDateObjectOnly,
        toDateParts, fromDateParts,
        toBooleanString,
        isValidCelularLength,
        toCpf,
        toCnpj
    }), [
        extractNumbers, 
        fromDateParts, 
        isValidDate, 
        toBooleanString, 
        toCelular, 
        toCompetencia, 
        toDate, 
        toDateObjectOnly, 
        toDateParts, 
        toDateTime, 
        toFloat, 
        toTelefone, 
        toTime,
        isValidCelularLength,
        toCpf,
        toCnpj
    ]);
}